<script lang="ts">
	import Paginator from "../Paginator.svelte";
	import DecoratedHeadline from "../DecoratedHeadline.svelte";
	import type { ActionReturn } from "svelte/action";
	import * as Glide from "@glidejs/glide";
	import { disableGlidePerSlideAmount } from "../../utils/disableGlidePerSlideAmount.js";
	import type { PublishedCaseStudy } from "../../../../core/schema/CaseStudy.js";
	import Reference from "./Reference.svelte";
	import { nbspify } from "../../../../core/utils/nbspify.js";

	export let title: string;
	export let similarReferences: PublishedCaseStudy[];

	const referencesPerPage = Math.min(2, similarReferences.length);
	const referencesPerPageSm = 1;

	let bulletCount = 2;

	let currentSlide = 1;
	let visibleSlides: number[] =
		typeof window !== "undefined" ? (window.innerWidth >= 1440 && similarReferences.length > 2 ? [1, 2] : [1]) : [];

	const perView =
		typeof window !== "undefined" ? (window.innerWidth < 1440 ? referencesPerPageSm : referencesPerPage) : 2;

	function glide(element: HTMLDivElement): ActionReturn {
		const glideOptions = {
			type: "slider",
			startAt: 0,
			focusAt: window.innerWidth < 1440 ? "center" : 0,
			perView,
			autoplay: false,
			hoverpause: false,
			animationDuration: 650,
			animationTimingFunc: "ease-in-out",
			keyboard: true,
			cloningRatio: 2,
			bound: true,
			gap: 10,
		} satisfies Partial<Glide.Options>;

		// eslint-disable-next-line new-cap
		const glide = new Glide.default(element, glideOptions).mount();
		glide.on("run", () => {
			currentSlide = glide.index;

			const { perView } = glide.settings;

			bulletCount = perView;
			visibleSlides =
				similarReferences.length < 3
					? [currentSlide + 1]
					: perView > 1
						? Array.from({ length: perView }, (_, i) => currentSlide + i)
						: [currentSlide + 1];
		});
		glide.on("resize", () => {
			currentSlide = glide.index;

			disableGlidePerSlideAmount(glide, element);
			if (window.innerWidth < 1440) {
				glide.update({ perView: referencesPerPageSm, focusAt: "center" });
			} else {
				glide.update({ perView: referencesPerPage, focusAt: 1 });
			}

			const { perView } = glide.settings;
			bulletCount = perView;
			visibleSlides =
				similarReferences.length < 3
					? [currentSlide + 1]
					: perView > 1
						? Array.from({ length: perView }, (_, i) => currentSlide + i)
						: [currentSlide + 1];
		});

		return {
			destroy(): void {
				glide.destroy();
			},
		};
	}
</script>

<div class="flex w-full flex-col items-center px-4 md:px-10">
	<div class="flex flex-col gap-8">
		<DecoratedHeadline>{nbspify(title)}</DecoratedHeadline>
		<slot name="perex" />
	</div>

	<div class="mt-[4rem] w-full max-w-[80rem]" use:glide>
		<div class="glide__track w-full overflow-hidden" data-glide-el="track">
			<div class="glide__slides flex">
				{#each similarReferences as reference}
					<div class="glide__slide flex items-center justify-center">
						<Reference {reference} />
					</div>
				{/each}
			</div>
		</div>
		<Paginator
			class="mx-auto mt-10 w-full max-w-[27.5rem]"
			{currentSlide}
			{perView}
			itemsLength={similarReferences.length}
			{visibleSlides}
			customBulletCount={bulletCount}
		/>
	</div>
</div>
